/* eslint-disable no-console */
import moment from "moment-timezone";
import getUserTimeZone from "./getUserTimeZone";

/**
 * Formats a validity object into a human-readable date range string,
 * converting UTC dates to the user's local timezone.
 * Example output: "May 22, 2025, 12:00 AM - Jun 17, 2025, 11:59 PM"
 *
 * @param {object} validity The validity object with dates in UTC
 * @param {string} validity.fromDate Start date in "YYYY-MM-DD" format (UTC)
 * @param {string} validity.toDate End date in "YYYY-MM-DD" format (UTC)
 * @param {string} [validity.startTime="00:00"] Start time in "HH:MM" format (UTC)
 * @param {string} [validity.endTime="23:59"] End time in "HH:MM" format (UTC)
 * @returns {string} The formatted date range string in user's local timezone
 */
const formatValidityPeriodWithUserTimeZone = (validity) => {
  if (!validity || !validity.fromDate || !validity.toDate) {
    return undefined;
  }

  try {
    // Extract values with defaults
    const { fromDate, toDate, startTime = "00:00", endTime = "23:59" } = validity;

    // Get user's timezone information
    const userTimezone = getUserTimeZone();

    // Create UTC moment objects
    const startMoment = moment.utc(`${fromDate}T${startTime}:00Z`);
    const endMoment = moment.utc(`${toDate}T${endTime}:00Z`);

    // Convert to user's timezone
    const localStartMoment = startMoment.clone().tz(userTimezone.zone);
    const localEndMoment = endMoment.clone().tz(userTimezone.zone);

    // Format in the requested style: "Mar 23, 2020, 12:00 AM"
    const formattedStart = localStartMoment.format("MMM DD, YYYY, h:mm A");
    const formattedEnd = localEndMoment.format("MMM DD, YYYY, h:mm A");

    // Combine into the final format
    return `${formattedStart} - ${formattedEnd}`;
  } catch (error) {
    console.error("Error formatting validity period:", error);
    return ""; // Return empty string on error
  }
};
const formatValidityPeriod = (validity) => {
  if (!validity || !validity.fromDate || !validity.toDate) {
    return undefined;
  }

  try {
    // Extract values with defaults
    const { fromDate, toDate, startTime = "00:00", endTime = "23:59" } = validity;

    // Create UTC moment objects
    const startMoment = moment.utc(`${fromDate}T${startTime}:00Z`);
    const endMoment = moment.utc(`${toDate}T${endTime}:00Z`);

    // Format in the requested style: "Mar 23, 2020, 12:00 AM"
    const formattedStart = startMoment.format("MMM DD, YYYY, h:mm A");
    const formattedEnd = endMoment.format("MMM DD, YYYY, h:mm A");

    // Combine into the final format
    return `${formattedStart} - ${formattedEnd}`;
  } catch (error) {
    console.error("Error formatting validity period:", error);
    return ""; // Return empty string on error
  }
};

/**
 * Alternative implementation using native Date objects if moment-timezone is not preferred
 */
const formatValidityPeriodNative = (validity) => {
  if (!validity || !validity.fromDate || !validity.toDate) {
    return "";
  }

  try {
    const { fromDate, toDate, startTime = "00:00", endTime = "23:59" } = validity;
    const userTimezone = getUserTimeZone();

    // Create Date objects from UTC strings
    const startDate = new Date(`${fromDate}T${startTime}:00Z`);
    const endDate = new Date(`${toDate}T${endTime}:00Z`);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      throw new Error("Invalid date or time input");
    }

    // Format options for toLocaleString
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: userTimezone.zone,
    };

    const formattedStart = startDate.toLocaleString("en-US", options);
    const formattedEnd = endDate.toLocaleString("en-US", options);

    return `${formattedStart} - ${formattedEnd}`;
  } catch (error) {
    console.error("Error formatting validity period:", error);
    return "";
  }
};

export { formatValidityPeriodWithUserTimeZone, formatValidityPeriodNative, formatValidityPeriod };
